import {effect, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {HubConnection, HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import {TokenService} from '../../../auth/token.service';
import {environment} from '../../../../environments/environment.dev';

/**
 * Étapes de la boucle agentique, poussées par /hubs/ai pendant qu'une question est traitée.
 * Fourni par le panneau de chat : la connexion ne vit que le temps où il est ouvert.
 */
@Injectable()
export class AiStepService implements OnDestroy {

  private readonly tokenService = inject(TokenService);

  /** Noms d'outils déjà appelés pour la question en cours, dans l'ordre. */
  readonly steps = signal<string[]>([]);

  private connection: HubConnection | null = null;

  /** Conversation suivie : les étapes des autres onglets du même utilisateur sont ignorées. */
  private watched: number | null = null;

  constructor() {
    // Le token est renouvelé en cours de session : la connexion doit repartir avec le nouveau,
    // sinon le hub reste muet jusqu'à la réouverture du panneau.
    effect(() => {
      const token = this.tokenService.token();

      if (token) { void this.connect(); } else { void this.disconnect(); }
    });
  }

  ngOnDestroy(): void {
    void this.disconnect();
  }

  watch(conversationId: number): void {
    this.watched = conversationId;
  }

  reset(): void {
    this.steps.set([]);
  }

  private async connect(): Promise<void> {
    await this.disconnect();

    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.apiUrl}/hubs/ai`, {accessTokenFactory: () => this.tokenService.token() ?? ''})
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('ReceiveAiStep', (conversationId: number, tool: string) => {
      if (this.watched !== conversationId) return;

      this.steps.update(steps => [...steps, tool]);
    });

    // Sans le canal temps réel le chat reste utilisable : on perd l'affichage des étapes, pas les réponses.
    try {
      await this.connection.start();
    } catch {
      this.connection = null;
    }
  }

  private async disconnect(): Promise<void> {
    const connection = this.connection;
    this.connection = null;

    await connection?.stop();
  }
}
